import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getDreamCategory, getAllDreamCategories } from '../../data/dreamData';
import DreamSubjectCard from '../../components/fortune/DreamSubjectCard';
import DreamBreadcrumb from '../../components/fortune/DreamBreadcrumb';
import AdBanner from '../../components/AdBanner';
import SEO from '../../components/SEO';
import './DreamCategoryPage.css';

function DreamCategoryPage() {
  const { categoryId } = useParams();
  const category = getDreamCategory(categoryId);

  if (!category) {
    return <Navigate to="/fortune/dream" replace />;
  }

  const otherCategories = getAllDreamCategories().filter(c => c.id !== category.id);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.seoTitle,
    description: category.seoDescription,
    url: `https://lottounse.kr/fortune/dream/${category.id}`,
    inLanguage: 'ko',
    author: { '@type': 'Organization', name: '로또운세' },
    datePublished: '2025-01-01',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: category.subjects.map((subject, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: subject.name,
        url: `https://lottounse.kr/fortune/dream/${category.id}/${subject.id}`,
      })),
    },
  };

  const midPoint = Math.floor(category.subjects.length / 2);

  return (
    <div className="fortune-category-page">
      <SEO
        title={category.seoTitle}
        description={category.seoDescription}
        keywords={`${category.name},꿈해몽,${category.subjects.map(s => s.name).join(',')}`}
        url={`https://lottounse.kr/fortune/dream/${category.id}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="category-header dream-header">
        <h1>{category.emoji} {category.name} 해몽</h1>
        <p>{category.description}</p>
      </div>

      <div className="category-content">
        <DreamBreadcrumb category={category} />

        <p className="seo-intro">{category.seoDescription}</p>

        <AdBanner />

        <h2 className="section-title">{category.emoji} {category.name} - 주제별 해몽</h2>
        <div className="cards-grid">
          {category.subjects.slice(0, midPoint).map((subject) => (
            <DreamSubjectCard
              key={subject.id}
              categoryId={category.id}
              subject={subject}
            />
          ))}
        </div>

        {category.subjects.length > 4 && <AdBanner />}

        <div className="cards-grid">
          {category.subjects.slice(midPoint).map((subject) => (
            <DreamSubjectCard
              key={subject.id}
              categoryId={category.id}
              subject={subject}
            />
          ))}
        </div>

        <AdBanner />

        {otherCategories.length > 0 && (
          <div className="related-section">
            <h3 className="section-title">📂 다른 카테고리 꿈해몽</h3>
            <div className="related-grid">
              {otherCategories.map(other => (
                <Link
                  key={other.id}
                  to={`/fortune/dream/${other.id}`}
                  className="related-card"
                >
                  <span className="related-emoji">{other.emoji}</span>
                  <span className="related-name">{other.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DreamCategoryPage;
